import { useEffect, useMemo, useState } from "react";
import { useFormConfigStore } from "../state/store";
import { useJobTypeForms, type JobTypeForm } from "./useJobTypeForms";
import {
  useFormPageConfig,
  type PageIdentityStrategy,
  type FormPageType,
  type FormCompletenessScenario,
} from "./useFormPageConfig";
import { ApiError } from "@/lib/api-client";
import { toast } from "@/components/Toaster";
import "./FormPagesTab.css";

const STRATEGY_OPTIONS: { value: PageIdentityStrategy; label: string }[] = [
  { value: "none", label: "None" },
  { value: "positional", label: "Positional" },
  { value: "fingerprint", label: "Fingerprint" },
  { value: "manual", label: "Manual" },
];

const STRATEGY_HELP =
  "fingerprint — requires per-page recognition; opt in only where useful.";

export function FormPagesTab() {
  const jobTypeId = useFormConfigStore((s) => s.version?.jobTypeId) ?? "";
  const { forms, loading: formsLoading } = useJobTypeForms(jobTypeId);

  const [selectedFormId, setSelectedFormId] = useState("");

  // Default the selection to the first form once it arrives.
  useEffect(() => {
    if (!selectedFormId && forms.length > 0) {
      setSelectedFormId(forms[0]!.id);
    }
  }, [forms, selectedFormId]);

  const selectedForm = forms.find((f) => f.id === selectedFormId);

  return (
    <div className="form-pages-tab">
      <div className="form-pages-tab__selector">
        <label htmlFor="form-pages-form-select" className="form-pages-tab__label">
          Form
        </label>
        <select
          id="form-pages-form-select"
          className="form-pages-tab__select"
          value={selectedFormId}
          onChange={(e) => setSelectedFormId(e.target.value)}
          disabled={formsLoading || forms.length === 0}
        >
          {forms.length === 0 && <option value="">No forms</option>}
          {forms.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>

      {selectedForm ? (
        <FormPagesEditor
          key={selectedForm.id}
          jobTypeId={jobTypeId}
          form={selectedForm}
        />
      ) : (
        <p className="form-pages-tab__empty">
          Select a form to configure its page types and completeness scenarios.
        </p>
      )}
    </div>
  );
}

function FormPagesEditor({
  jobTypeId,
  form,
}: {
  jobTypeId: string;
  form: JobTypeForm;
}) {
  const {
    pageTypes,
    scenarios,
    createPageType,
    updatePageType,
    deletePageType,
    createScenario,
    deleteScenario,
    patchForm,
  } = useFormPageConfig(jobTypeId, form.id);

  // Local mirror of the two form-level fields so the toggle/select reflect
  // optimistic state immediately (the parent `forms` list isn't refetched).
  const [allowsAttachments, setAllowsAttachments] = useState(
    form.allowsAttachments ?? false,
  );
  const [enforcePageOrder, setEnforcePageOrder] = useState(
    form.enforcePageOrder ?? false,
  );
  const [autoReorderPages, setAutoReorderPages] = useState(
    form.autoReorderPages ?? false,
  );
  const [strategy, setStrategy] = useState<PageIdentityStrategy>(
    form.pageIdentityStrategy ?? "none",
  );
  // E-053 — park (default) vs continue-with-flag for incomplete assemblies.
  const [incompleteHandling, setIncompleteHandling] = useState<"park" | "continue">(
    form.incompleteHandling ?? "park",
  );

  const pageTypeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const pt of pageTypes) m.set(pt.id, pt.name);
    return m;
  }, [pageTypes]);

  async function handleStrategyChange(next: PageIdentityStrategy) {
    setStrategy(next);
    try {
      await patchForm({ pageIdentityStrategy: next });
    } catch {
      toast.error("Could not update page identity strategy.");
    }
  }

  async function handleAttachmentsChange(next: boolean) {
    setAllowsAttachments(next);
    try {
      await patchForm({ allowsAttachments: next });
    } catch {
      toast.error("Could not update attachment setting.");
    }
  }

  async function handleEnforcePageOrderChange(next: boolean) {
    setEnforcePageOrder(next);
    try {
      await patchForm({ enforcePageOrder: next });
    } catch {
      toast.error("Could not update page order setting.");
    }
  }

  async function handleAutoReorderPagesChange(next: boolean) {
    setAutoReorderPages(next);
    try {
      await patchForm({ autoReorderPages: next });
    } catch {
      toast.error("Could not update auto-reorder setting.");
    }
  }

  async function handleIncompleteHandlingChange(next: "park" | "continue") {
    setIncompleteHandling(next);
    try {
      await patchForm({ incompleteHandling: next });
    } catch {
      toast.error("Could not update incomplete-document handling.");
    }
  }

  async function handleDeletePageType(id: string) {
    try {
      await deletePageType(id);
    } catch (e) {
      if (e instanceof ApiError && (e.body as { error?: string })?.error === "page_type_in_use") {
        toast.error("This page type is used by a completeness scenario and can't be deleted.");
        return;
      }
      toast.error("Could not delete page type.");
    }
  }

  async function handleDeleteScenario(id: string) {
    try {
      await deleteScenario(id);
    } catch {
      toast.error("Could not delete scenario.");
    }
  }

  return (
    <div className="form-pages-tab__editor">
      {/* Form-level settings */}
      <section className="form-pages-tab__panel" aria-labelledby="form-pages-settings-h">
        <h3 id="form-pages-settings-h" className="form-pages-tab__panel-title">
          Settings
        </h3>

        <div className="form-pages-tab__field">
          <label className="form-pages-tab__toggle-label" htmlFor="form-pages-attachments">
            <input
              id="form-pages-attachments"
              type="checkbox"
              checked={allowsAttachments}
              onChange={(e) => void handleAttachmentsChange(e.target.checked)}
            />
            <span>Allow attachments</span>
          </label>
        </div>

        <div className="form-pages-tab__field">
          <label className="form-pages-tab__toggle-label" htmlFor="form-pages-enforce-order">
            <input
              id="form-pages-enforce-order"
              type="checkbox"
              checked={enforcePageOrder}
              onChange={(e) => void handleEnforcePageOrderChange(e.target.checked)}
            />
            <span>Enforce page order</span>
          </label>
        </div>

        <div className="form-pages-tab__field">
          <label className="form-pages-tab__toggle-label" htmlFor="form-pages-auto-reorder">
            <input
              id="form-pages-auto-reorder"
              type="checkbox"
              checked={autoReorderPages}
              disabled={!enforcePageOrder}
              onChange={(e) => void handleAutoReorderPagesChange(e.target.checked)}
            />
            <span>Auto-reorder pages</span>
          </label>
          <p className="form-pages-tab__help">
            Automatically reorder a misordered document&apos;s pages to the expected sequence.
          </p>
        </div>

        <div className="form-pages-tab__field">
          <label htmlFor="form-pages-strategy" className="form-pages-tab__label">
            Page identity strategy
          </label>
          <select
            id="form-pages-strategy"
            className="form-pages-tab__select"
            value={strategy}
            onChange={(e) => void handleStrategyChange(e.target.value as PageIdentityStrategy)}
          >
            {STRATEGY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="form-pages-tab__help">{STRATEGY_HELP}</p>
        </div>

        {/* E-053 — park (default) vs continue-with-flag. Inert (not hidden)
            while page identity is off, mirroring the autoReorderPages gate. */}
        <fieldset className="form-pages-tab__field form-pages-tab__radio-group">
          <legend className="form-pages-tab__label">When a document is incomplete</legend>
          <label className="form-pages-tab__toggle-label" htmlFor="form-pages-incomplete-park">
            <input
              id="form-pages-incomplete-park"
              type="radio"
              name="incomplete-handling"
              value="park"
              checked={incompleteHandling === "park"}
              disabled={strategy === "none"}
              onChange={() => void handleIncompleteHandlingChange("park")}
            />
            <span>Park for repair</span>
          </label>
          <p className="form-pages-tab__help">
            Hold the document at the Classify station until an operator fixes or accepts its
            pages. (default)
          </p>
          <label className="form-pages-tab__toggle-label" htmlFor="form-pages-incomplete-continue">
            <input
              id="form-pages-incomplete-continue"
              type="radio"
              name="incomplete-handling"
              value="continue"
              checked={incompleteHandling === "continue"}
              disabled={strategy === "none"}
              onChange={() => void handleIncompleteHandlingChange("continue")}
            />
            <span>Continue with flag</span>
          </label>
          <p className="form-pages-tab__help">
            Let the document flow through the pipeline; rules can read Completeness and Missing
            page types to stamp an exception code, hold, or notify.
          </p>
          <p className="form-pages-tab__help">
            Applies when page identity runs (a strategy is selected and page types exist).
            Misordered and unclassifiable documents still park.
          </p>
        </fieldset>
      </section>

      {/* Page types */}
      <PageTypesPanel
        pageTypes={pageTypes}
        onCreate={createPageType}
        onRename={updatePageType}
        onDelete={handleDeletePageType}
      />

      {/* Scenarios */}
      <ScenariosPanel
        scenarios={scenarios}
        pageTypeById={pageTypeById}
        pageTypes={pageTypes}
        onCreate={createScenario}
        onDelete={handleDeleteScenario}
      />
    </div>
  );
}

/**
 * One editable page-type row. Keeps the name/ordinal inputs in local state
 * and commits the PATCH only on blur or Enter (and only when the value
 * actually changed) — editing on every keystroke spammed PATCH and the
 * post-PATCH `load()` refetch clobbered the in-flight edit.
 */
function PageTypeRow({
  pageType,
  onRename,
  onDelete,
}: {
  pageType: FormPageType;
  onRename: (id: string, args: { name?: string; ordinal?: number; isDocumentStart?: boolean }) => Promise<unknown>;
  onDelete: (id: string) => void;
}) {
  const [name, setName] = useState(pageType.name);
  const [ordinal, setOrdinal] = useState(String(pageType.ordinal));

  // Re-sync local state when the upstream row changes (e.g. after a refetch
  // that reorders/renames from elsewhere).
  useEffect(() => {
    setName(pageType.name);
  }, [pageType.name]);
  useEffect(() => {
    setOrdinal(String(pageType.ordinal));
  }, [pageType.ordinal]);

  function commitName() {
    const trimmed = name.trim();
    if (trimmed && trimmed !== pageType.name) {
      void onRename(pageType.id, { name: trimmed });
    } else {
      setName(pageType.name);
    }
  }

  function commitOrdinal() {
    const next = Number(ordinal);
    if (Number.isFinite(next) && next !== pageType.ordinal) {
      void onRename(pageType.id, { ordinal: next });
    } else {
      setOrdinal(String(pageType.ordinal));
    }
  }

  return (
    <li className="form-pages-tab__row">
      <input
        className="form-pages-tab__inline-input form-pages-tab__ordinal"
        type="number"
        value={ordinal}
        aria-label={`Ordinal for ${pageType.name}`}
        onChange={(e) => setOrdinal(e.target.value)}
        onBlur={commitOrdinal}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
      <input
        className="form-pages-tab__inline-input form-pages-tab__name"
        value={name}
        aria-label={`Name for ${pageType.name}`}
        onChange={(e) => setName(e.target.value)}
        onBlur={commitName}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
      <label className="form-pages-tab__doc-start" htmlFor={`doc-start-${pageType.id}`}>
        <input
          id={`doc-start-${pageType.id}`}
          type="checkbox"
          aria-label={`Doc start for ${pageType.name}`}
          checked={pageType.isDocumentStart ?? false}
          onChange={(e) => void onRename(pageType.id, { isDocumentStart: e.target.checked })}
        />
        <span>Doc start</span>
      </label>
      <button
        type="button"
        className="form-pages-tab__btn form-pages-tab__btn--danger"
        aria-label={`Delete page type ${pageType.name}`}
        onClick={() => onDelete(pageType.id)}
      >
        Delete
      </button>
    </li>
  );
}

function PageTypesPanel({
  pageTypes,
  onCreate,
  onRename,
  onDelete,
}: {
  pageTypes: FormPageType[];
  onCreate: (args: { name: string; ordinal: number }) => Promise<unknown>;
  onRename: (id: string, args: { name?: string; ordinal?: number; isDocumentStart?: boolean }) => Promise<unknown>;
  onDelete: (id: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [ordinal, setOrdinal] = useState("0");

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) return;
    await onCreate({ name: trimmed, ordinal: Number(ordinal) });
    setName("");
    setOrdinal("0");
    setAdding(false);
  }

  return (
    <section className="form-pages-tab__panel" aria-labelledby="form-pages-pt-h">
      <div className="form-pages-tab__panel-head">
        <h3 id="form-pages-pt-h" className="form-pages-tab__panel-title">
          Page types
        </h3>
        {!adding && (
          <button
            type="button"
            className="form-pages-tab__btn"
            onClick={() => setAdding(true)}
          >
            + Add page type
          </button>
        )}
      </div>

      {pageTypes.length === 0 && !adding && (
        <p className="form-pages-tab__empty">No page types yet.</p>
      )}

      <ul className="form-pages-tab__list">
        {pageTypes.map((pt) => (
          <PageTypeRow key={pt.id} pageType={pt} onRename={onRename} onDelete={onDelete} />
        ))}
      </ul>

      {adding && (
        <div className="form-pages-tab__add-row">
          <div className="form-pages-tab__field">
            <label htmlFor="new-page-type-ordinal" className="form-pages-tab__label">
              Ordinal
            </label>
            <input
              id="new-page-type-ordinal"
              className="form-pages-tab__inline-input form-pages-tab__ordinal"
              type="number"
              value={ordinal}
              aria-label="Page type ordinal"
              onChange={(e) => setOrdinal(e.target.value)}
            />
          </div>
          <div className="form-pages-tab__field form-pages-tab__field--grow">
            <label htmlFor="new-page-type-name" className="form-pages-tab__label">
              Name
            </label>
            <input
              id="new-page-type-name"
              className="form-pages-tab__inline-input"
              value={name}
              aria-label="Page type name"
              placeholder="e.g. Cover"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="form-pages-tab__btn form-pages-tab__btn--primary"
            onClick={() => void handleSave()}
          >
            Save page type
          </button>
          <button
            type="button"
            className="form-pages-tab__btn"
            onClick={() => {
              setAdding(false);
              setName("");
              setOrdinal("0");
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}

function ScenariosPanel({
  scenarios,
  pageTypeById,
  pageTypes,
  onCreate,
  onDelete,
}: {
  scenarios: FormCompletenessScenario[];
  pageTypeById: Map<string, string>;
  pageTypes: FormPageType[];
  onCreate: (args: { name: string; pageTypeIds: string[] }) => Promise<unknown>;
  onDelete: (id: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (selected.size === 0) return;
    await onCreate({ name: trimmed, pageTypeIds: [...selected] });
    setName("");
    setSelected(new Set());
    setAdding(false);
  }

  return (
    <section className="form-pages-tab__panel" aria-labelledby="form-pages-sc-h">
      <div className="form-pages-tab__panel-head">
        <h3 id="form-pages-sc-h" className="form-pages-tab__panel-title">
          Completeness scenarios
        </h3>
        {!adding && (
          <button
            type="button"
            className="form-pages-tab__btn"
            onClick={() => setAdding(true)}
          >
            + Add a scenario
          </button>
        )}
      </div>

      {scenarios.length === 0 && !adding && (
        <p className="form-pages-tab__empty">No scenarios yet.</p>
      )}

      <ul className="form-pages-tab__list">
        {scenarios.map((sc) => (
          <li key={sc.id} className="form-pages-tab__row">
            <span className="form-pages-tab__scenario-name">{sc.name}</span>
            <span className="form-pages-tab__scenario-pts">
              {sc.pageTypeIds.map((id) => pageTypeById.get(id) ?? id).join(", ") || "—"}
            </span>
            <button
              type="button"
              className="form-pages-tab__btn form-pages-tab__btn--danger"
              aria-label={`Delete scenario ${sc.name}`}
              onClick={() => onDelete(sc.id)}
            >
              Delete
            </button>
          </li>
        ))}
      </ul>

      {adding && (
        <div className="form-pages-tab__add-scenario">
          <div className="form-pages-tab__field form-pages-tab__field--grow">
            <label htmlFor="new-scenario-name" className="form-pages-tab__label">
              Scenario name
            </label>
            <input
              id="new-scenario-name"
              className="form-pages-tab__inline-input"
              value={name}
              aria-label="Scenario name"
              placeholder="e.g. Standard"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <fieldset className="form-pages-tab__multiselect">
            <legend className="form-pages-tab__label">Required page types</legend>
            {pageTypes.length === 0 && (
              <p className="form-pages-tab__help">Add page types first.</p>
            )}
            {pageTypes.map((pt) => (
              <label key={pt.id} className="form-pages-tab__checkbox">
                <input
                  type="checkbox"
                  checked={selected.has(pt.id)}
                  aria-label={`Select page type ${pt.name}`}
                  onChange={() => toggle(pt.id)}
                />
                <span>{pt.name}</span>
              </label>
            ))}
          </fieldset>

          <div className="form-pages-tab__add-actions">
            <button
              type="button"
              className="form-pages-tab__btn form-pages-tab__btn--primary"
              onClick={() => void handleSave()}
            >
              Save scenario
            </button>
            <button
              type="button"
              className="form-pages-tab__btn"
              onClick={() => {
                setAdding(false);
                setName("");
                setSelected(new Set());
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
